import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminUserStatisticsQuery } from '../../queries/appQueries';
import type { UserStatistics } from '../../services/userManagementService';
import {
  Text,
  Group,
  Stack,
  useMantineColorScheme,
  Box,
  Card,
  SimpleGrid,
  RingProgress,
  Alert,
} from '@mantine/core';
import PageTransition from '../../components/animations/PageTransition';
import PageSkeleton from '../../components/ui/PageSkeleton';
import CountUpNumber from '../../components/animations/CountUpNumber';
import AdminPageHeader from '../../components/AdminPageHeader';
import {
  IconUsers,
  IconPlugConnected,
  IconUserPlus,
  IconAlertCircle,
} from '@tabler/icons-react';

// Colores SOLO para la gráfica de distribución (las cards van en paleta sobria).
const PLAN_COLORS = ['blue', 'teal', 'orange', 'grape', 'cyan', 'pink', 'lime', 'gray'];
const PLAN_HEX: Record<string, string> = {
  blue: '#339af0',
  teal: '#20c997',
  orange: '#fd7e14',
  grape: '#cc5de8',
  cyan: '#22b8cf',
  pink: '#f06595',
  lime: '#94d82d',
  gray: '#868e96',
};

const UserStatisticsPage: React.FC = () => {
  const { colorScheme } = useMantineColorScheme();
  const userStatsResult = useQuery(adminUserStatisticsQuery());
  const stats = userStatsResult.data as UserStatistics | undefined;
  const loading = userStatsResult.isLoading;
  const queryError = userStatsResult.error;
  const error = queryError ? 'No se pudieron cargar las estadísticas. Intenta de nuevo.' : null;

  // Paleta sobria unificada con el perfil de usuario y el dashboard de admin.
  const cardStyle: React.CSSProperties = {
    backgroundColor: colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.7)' : 'rgba(247, 243, 238, 0.95)',
    border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
    boxShadow: 'none',
  };
  const textColor = colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26';
  const mutedColor = colorScheme === 'dark' ? '#94a3b8' : '#8c8478';
  const sectionLabelStyle: React.CSSProperties = {
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: mutedColor,
    fontFamily: 'Inter, sans-serif',
  };

  if (loading) {
    return <PageSkeleton variant="grid" count={3} />;
  }

  const planEntries = stats
    ? Object.entries(stats.distribucionPorPlan).filter(
        ([name, count]) => count > 0 && name.toLowerCase() !== 'plan mensual'
      )
    : [];
  const totalPlanUsers = planEntries.reduce((acc, [, c]) => acc + c, 0);
  const planSections = planEntries.map(([name, count], index) => {
    const pct = totalPlanUsers > 0 ? (count / totalPlanUsers) * 100 : 0;
    const color = PLAN_COLORS[index % PLAN_COLORS.length];
    return { name, count, pct, color };
  });

  const statsCards = [
    { icon: IconUsers, value: stats?.totalUsuarios ?? 0, label: 'Total de Usuarios' },
    { icon: IconPlugConnected, value: stats?.sesionesActivas ?? 0, label: 'Sesiones Activas' },
    { icon: IconUserPlus, value: stats?.nuevosEsteMes ?? 0, label: 'Nuevos Este Mes' },
  ];

  return (
    <PageTransition type="medical" duration={800}>
      <Box style={{ flex: 1, padding: '16px 24px', overflowY: 'auto' }}>
        <Stack gap="lg">
          <AdminPageHeader
            title="Estadísticas de Usuarios"
            subtitle="Análisis y métricas de rendimiento estudiantil"
          />

          {error && (
            <Alert icon={<IconAlertCircle size={18} />} color="red" variant="light" radius="lg">
              {error}
            </Alert>
          )}

          {/* Cards de resumen (paleta sobria, icono negro arriba-izquierda) */}
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
            {statsCards.map((s) => (
              <Card key={s.label} withBorder radius="lg" p="lg" style={cardStyle}>
                <Stack gap="sm" align="stretch">
                  <s.icon size={20} color={textColor} style={{ flexShrink: 0 }} />
                  <Box style={{ minWidth: 0 }}>
                    <CountUpNumber
                      value={s.value}
                      duration={1500}
                      style={{
                        color: textColor,
                        fontSize: '1.6rem',
                        fontWeight: 700,
                        fontFamily: 'Space Grotesk, Inter, sans-serif',
                        textAlign: 'left',
                      }}
                    />
                    <Text size="xs" ta="left" style={{ color: mutedColor, fontFamily: 'Inter, sans-serif' }}>
                      {s.label}
                    </Text>
                  </Box>
                </Stack>
              </Card>
            ))}
          </SimpleGrid>

          {/* Distribución de suscripciones — aquí SÍ van colores en la gráfica */}
          <Card withBorder radius="lg" p="lg" style={cardStyle}>
            <Text size="xs" fw={700} ta="left" mb="lg" style={sectionLabelStyle}>
              Distribución de Suscripciones
            </Text>

            {planSections.length === 0 ? (
              <Text size="sm" ta="center" py="lg" style={{ color: mutedColor }}>
                No hay datos de suscripciones para mostrar.
              </Text>
            ) : (
              <>
                <Group justify="center" mb="lg">
                  <RingProgress
                    size={160}
                    thickness={16}
                    sections={planSections.map((s) => ({
                      value: s.pct,
                      color: s.color,
                      tooltip: `${s.name} - ${s.pct.toFixed(1)}%`,
                    }))}
                    label={
                      <Text ta="center" fw={700} size="md" style={{ color: textColor }}>
                        Activas
                      </Text>
                    }
                  />
                </Group>

                <Stack gap="sm">
                  {planSections.map((s) => (
                    <Group key={s.name} justify="space-between">
                      <Group gap="xs">
                        <Box
                          w={10}
                          h={10}
                          style={{ backgroundColor: PLAN_HEX[s.color] ?? '#868e96', borderRadius: '50%' }}
                        />
                        <Text size="xs" style={{ color: mutedColor }}>
                          {s.name}
                        </Text>
                      </Group>
                      <Text size="xs" fw={600} style={{ color: textColor }}>
                        {s.count.toLocaleString()} usuarios ({s.pct.toFixed(1)}%)
                      </Text>
                    </Group>
                  ))}
                </Stack>
              </>
            )}
          </Card>
        </Stack>
      </Box>
    </PageTransition>
  );
};

export default UserStatisticsPage;
